import React, { useState, useEffect } from 'react';
import invoiceService from 'services/invoiceService';
import providerService from 'services/providerService';
import paymentService from 'services/paymentService';
import Swal from 'sweetalert2';
import 'styles/manager.css';

export const AdminDashboardView = () => {
  const [stats, setStats] = useState({ activeProviders: 0, pendingReview: 0, totalAmount: 0, approved: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [statusSummary, setStatusSummary] = useState({ recibida: 0, en_revision: 0, aprobada: 0, pagada: 0, rechazada: 0 });
  const [loading, setLoading] = useState(true);

  // ── Top Spending ──────────────────────────────────────────────────────────
  const [topSpending, setTopSpending] = useState([]);
  const [topGroupBy, setTopGroupBy] = useState('proveedor');
  const [topLoading, setTopLoading] = useState(false);

  useEffect(() => { loadDashboardData(); }, []);
  useEffect(() => { loadTopSpending(); }, [topGroupBy]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const statsResponse = await invoiceService.getInvoiceStats();
      if (!statsResponse.error) {
        const data = statsResponse.data.data;
        setStats({
          activeProviders: 0,
          pendingReview: Number(data.recibidas || 0) + Number(data.en_revision || 0),
          totalAmount: data.monto_total || 0,
          approved: data.aprobadas || 0
        });
        setStatusSummary({
          recibida: data.recibidas || 0,
          en_revision: data.en_revision || 0,
          aprobada: data.aprobadas || 0,
          pagada: data.pagadas || 0,
          rechazada: data.rechazadas || 0
        });
      }

      const providersResponse = await providerService.getProviders({ activo: true }); 
      if (!providersResponse.error) {
        const providers = providersResponse.data.data || [];
        setStats(prev => ({ ...prev, activeProviders: providersResponse.data.pagination.totalCount }));
      }

      const invoicesResponse = await invoiceService.getInvoices({ page: 1, limit: 5, orderBy: 'created_at', orderDir: 'DESC' });
      if (!invoicesResponse.error) setRecentActivity(invoicesResponse.data.data || []);
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo cargar la información del dashboard', timer: 3000, showConfirmButton: false });
    } finally {
      setLoading(false);
    }
  };

  const loadTopSpending = async () => {
    try {
      setTopLoading(true);

      const res = await paymentService.getTopSpendingReport({
        top: 8,
        agrupar_por: topGroupBy
      });

      if (!res.error) {

        // 🔹 Ahora sí accedemos correctamente al array
        const list = res.data?.data?.top || [];
        const granTotal = res.data?.data?.gran_total || 0;

        const maxAmount = Math.max(
          ...list.map(d => Number(d.total_monto) || 0),
          1
        );

        const normalized = list.map(d => ({
          ...d,
          total_monto: Number(d.total_monto) || 0,
          porcentaje: Number(d.porcentaje) || 0,
          pct: Math.round(((Number(d.total_monto) || 0) / maxAmount) * 100)
        }));

        setTopSpending(normalized);
      }

    } catch (error) {
      console.error('Error al cargar top spending', error);
    } finally {
      setTopLoading(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '';
    return new Date(s).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const formatCurrency = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

  const statusTranslations = { recibida: 'Recibida', en_revision: 'En Revisión', aprobada: 'Aprobada', rechazada: 'Rechazada', pagada: 'Pagada' };

  // Colores para el top spending
  const topColors = ['#8b6f47', '#a0845e', '#b59878', '#c9ac90', '#6f8b47', '#47788b', '#47508b', '#8b4765'];

  if (loading) {
    return (
      <div className="admin-dashboard-view">
        <div className="loading-state"><div className="spinner"></div><p>Cargando información...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-view">

      {/* ── KPI cards ── */}
      <div className="stats-grid">

        <div className="stat-card">
          <div className="stat-label">
            <i className="bi bi-people-fill me-2"></i>
            Proveedores Activos
          </div>
          <div className="stat-value">{stats.activeProviders}</div>
          <div className="stat-change">
            <i className="bi bi-person-check me-1"></i>
            Total registrados
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">
            <i className="bi bi-receipt me-2"></i>
            Facturas Pendientes
          </div>
          <div className="stat-value">{stats.pendingReview}</div>
          <div className={`stat-change ${stats.pendingReview > 0 ? 'negative' : 'positive'}`}>
            {stats.pendingReview > 0 ? (
              <>
                <i className="bi bi-exclamation-triangle-fill me-1 text-warning"></i>
                Requieren atención
              </>
            ) : (
              <>
                <i className="bi bi-check-circle-fill me-1 text-success"></i>
                Todo al día
              </>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">
            <i className="bi bi-currency-dollar me-2"></i>
            Total Facturado
          </div>
          <div className="stat-value">{formatCurrency(stats.totalAmount)}</div>
          <div className="stat-change positive">
            <i className="bi bi-graph-up-arrow me-1"></i>
            Acumulado total
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">
            <i className="bi bi-patch-check-fill me-2"></i>
            Facturas Aprobadas
          </div>
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-change positive">
            <i className="bi bi-cash-stack me-1"></i>
            Listas para pago
          </div>
        </div>

      </div>

      {/* ── TOP SPENDING ── */}
      <div className="card mb-4">
        <div className="card-header">
          <i className="bi bi-trophy-fill me-2 "></i>
          Top Gastos
        </div>
        {/* HEADER */}
        <div className="card-header">
          <div className="row align-items-center g-2">
            {/* Botones */}
            <div className="btn-group flex-wrap">
              {[
                { id: 'proveedor', label: 'Proveedor', icon: 'bi-building' },
                { id: 'tipo_gasto', label: 'Tipo Gasto', icon: 'bi-tags-fill' },
                { id: 'centro_costo', label: 'Centro Costo', icon: 'bi-diagram-3-fill' }
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setTopGroupBy(opt.id)}
                  className={`btn btn-sm ${topGroupBy === opt.id
                    ? 'btn-dark'
                    : 'btn-outline-secondary'
                    }`}
                >
                  <i className={`bi ${opt.icon} me-1`}></i>
                  {opt.label}
                </button>
              ))}
            </div>

          </div>

        </div>

        {/* BODY */}
        <div className="card-body">

          {topLoading ? (
            <div className="text-center text-muted py-4">
              <div className="spinner-border mb-2"></div>
              <div>
                <i className="bi bi-hourglass-split me-1"></i>
                Cargando datos...
              </div>
            </div>
          ) : topSpending.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-bar-chart-line-fill fs-1 text-muted"></i>
              <div className="mt-2 text-muted">
                No hay datos de gastos disponibles
              </div>
            </div>
          ) : (
            <div className="d-flex flex-column gap-4">

              {topSpending.map((item, idx) => (
                <div key={idx}>

                  <div className="row align-items-center">

                    {/* Nombre */}
                    <div className="col-12 col-md-6 d-flex align-items-center gap-2 mb-2 mb-md-0">

                      <div
                        className="d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: topColors[idx % topColors.length],
                          fontSize: '0.75rem'
                        }}
                      >
                        {idx + 1}
                      </div>

                      <div>
                        <div className="fw-semibold">
                          {item.nombre ||
                            item.proveedor_nombre ||
                            item.tipo_gasto_nombre ||
                            item.centro_costo_nombre ||
                            `#${idx + 1}`}
                        </div>

                        {item.facturas_pagadas && (
                          <div className="small text-muted">
                            <i className="bi bi-file-earmark-text me-1"></i>
                            {item.facturas_pagadas} facturas pagadas
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Monto */}
                    <div className="col-12 col-md-6 text-md-end">
                      <div className="fw-bold">
                        {formatCurrency(item.total_monto)}
                      </div>

                      {item.porcentaje && (
                        <div className="small text-muted">
                          <i className="bi bi-pie-chart-fill me-1"></i>
                          {item.porcentaje}% del total
                        </div>
                      )}
                    </div>

                  </div>

                  {/* Progress */}
                  <div className="progress mt-2" style={{ height: 8 }}>
                    <div
                      className="progress-bar"
                      style={{
                        width: `${item.pct}%`,
                        background: topColors[idx % topColors.length]
                      }}
                    />
                  </div>

                </div>
              ))}

            </div>
          )}

        </div>

      </div>
      {/* ── Actividad reciente ── */}
      <div className="card">
        <div className="card-header">
          <i className="bi bi-clock-history me-2"></i>
          Actividad Global Reciente
        </div>

        <div className="timeline">
          {recentActivity.length === 0 ? (
            <div className="text-center p-4">
              <i className="bi bi-clipboard-x fs-1 text-muted"></i>
              <div className="mt-2">No hay actividad reciente</div>
            </div>
          ) : (
            recentActivity.map(invoice => (
              <div key={invoice.id} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="timeline-date">
                    <i className="bi bi-calendar-event me-1"></i>
                    {formatDate(invoice.created_at)}
                  </div>

                  <div className="timeline-title">
                    <i className="bi bi-receipt-cutoff me-1"></i>
                    {invoice.folio} - {statusTranslations[invoice.estatus]}
                  </div>

                  <p className="timeline-description">
                    <i className="bi bi-building me-1"></i>
                    {invoice.proveedor_nombre} — {formatCurrency(invoice.total)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Resumen por estatus ── */}
      <div className="stats-grid">
        <div className="card status-summary">
          <div className="card-header">
            <i className="bi bi-kanban-fill me-2"></i>
            Resumen por Estatus
          </div>

          <div className="status-grid">

            <div className="status-box status-box-recibida">
              <i className="bi bi-inbox-fill mb-1"></i>
              <div className="status-count">{statusSummary.recibida}</div>
              <div className="status-label">Recibidas</div>
            </div>

            <div className="status-box status-box-revision">
              <i className="bi bi-search mb-1"></i>
              <div className="status-count">{statusSummary.en_revision}</div>
              <div className="status-label">En Revisión</div>
            </div>

            <div className="status-box status-box-aprobada">
              <i className="bi bi-check-circle-fill mb-1 text-success"></i>
              <div className="status-count">{statusSummary.aprobada}</div>
              <div className="status-label">Aprobadas</div>
            </div>

            <div className="status-box status-box-pagada">
              <i className="bi bi-cash-coin mb-1 text-success"></i>
              <div className="status-count">{statusSummary.pagada}</div>
              <div className="status-label">Pagadas</div>
            </div>

            <div className="status-box status-box-rechazada">
              <i className="bi bi-x-circle-fill mb-1 text-danger"></i>
              <div className="status-count">{statusSummary.rechazada}</div>
              <div className="status-label">Rechazadas</div>
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};